
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Shield, ShieldCheck, Smartphone, Copy, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TwoFactorAuth = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    checkTwoFactorStatus();
  }, []);

  const checkTwoFactorStatus = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) {
        console.error('Error listing 2FA factors:', error);
        return;
      }
      const totp = data?.totp?.[0];
      if (totp) {
        setFactorId(totp.id);
        setIsEnabled(totp.status === 'verified');
      } else {
        setIsEnabled(false);
      }
    } catch (error) {
      console.error('Error checking 2FA status:', error);
    }
  };

  const enableTwoFactor = async () => {
    setIsLoading(true);
    try {
      // If a TOTP factor already exists, don't enroll again
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const existingTotp = factors?.totp?.[0];
      if (existingTotp) {
        setFactorId(existingTotp.id);
        if (existingTotp.status === 'verified') {
          setIsEnabled(true);
          toast.info('2FA is already enabled for this account.');
          setIsLoading(false);
          return;
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Genova AI Account'
      });

      if (error) {
        toast.error('Failed to enable 2FA: ' + error.message);
        return;
      }

      if (data) {
        setFactorId(data.id);
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
        setShowSetup(true);
        toast.success('2FA setup initiated. Please scan the QR code with your authenticator app.');
      }
    } catch (error: any) {
      toast.error('An error occurred while enabling 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyAndComplete = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    try {
      // Start a TOTP challenge then verify with the provided code
      if (!factorId) {
        const { data } = await supabase.auth.mfa.listFactors();
        const existing = data?.totp?.[0];
        if (existing) setFactorId(existing.id);
      }

      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: factorId || ''
      } as any);
      if (challengeError || !challenge) {
        toast.error('Failed to start verification. Please try again.');
        setIsLoading(false);
        return;
      }

      const challengeId = (challenge as any).id || (challenge as any).challengeId;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId || '',
        challengeId,
        code: verificationCode
      } as any);

      if (verifyError) {
        toast.error('Invalid verification code. Please try again.');
        return;
      }

      setIsEnabled(true);
      setShowSetup(false);
      toast.success('Two-Factor Authentication enabled successfully!');
    } catch (error: any) {
      toast.error('Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const disableTwoFactor = async () => {
    setIsLoading(true);
    try {
      // Ensure we have a factorId
      let idToDisable = factorId;
      if (!idToDisable) {
        const { data } = await supabase.auth.mfa.listFactors();
        idToDisable = data?.totp?.[0]?.id || '';
      }

      if (!idToDisable) {
        toast.error('No 2FA factor found to disable.');
        setIsLoading(false);
        return;
      }

      const { error } = await supabase.auth.mfa.unenroll({ factorId: idToDisable });

      if (error) {
        toast.error('Failed to disable 2FA: ' + error.message);
        return;
      }

      setIsEnabled(false);
      setShowSetup(false);
      setFactorId('');
      setSecret('');
      toast.success('Two-Factor Authentication disabled successfully');
    } catch (error: any) {
      toast.error('An error occurred while disabling 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    toast.success('Secret key copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (showSetup) {
    return (
      <Card className="genova-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Smartphone className="w-5 h-5 text-genova-blue" />
            <span>Set Up Two-Factor Authentication</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Shield className="w-4 h-4" />
            <AlertDescription>
              Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)
            </AlertDescription>
          </Alert>

          <div className="text-center space-y-4">
            {qrCode && (
              <div className="flex justify-center">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 border rounded-lg" />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Or enter this secret key manually:</Label>
              <div className="flex items-center space-x-2">
                <Input
                  value={secret}
                  readOnly
                  className="genova-input font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copySecret}
                  className="shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="verificationCode">Enter verification code from your app:</Label>
            <Input
              id="verificationCode"
              type="text"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="genova-input text-center text-lg tracking-widest"
              maxLength={6}
            />
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={verifyAndComplete}
              disabled={isLoading || verificationCode.length !== 6}
              className="genova-button flex-1"
            >
              {isLoading ? 'Verifying...' : 'Complete Setup'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSetup(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="genova-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-genova-purple" />
            <span>Two-Factor Authentication</span>
          </div>
          <Badge variant={isEnabled ? "default" : "secondary"} className={isEnabled ? "bg-green-500" : ""}>
            {isEnabled ? (
              <>
                <ShieldCheck className="w-3 h-3 mr-1" />
                Enabled
              </>
            ) : (
              'Disabled'
            )}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Add an extra layer of security to your account with two-factor authentication using your mobile device.
        </p>
        
        {isEnabled ? (
          <Button
            variant="outline"
            onClick={disableTwoFactor}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Disabling...' : 'Disable 2FA'}
          </Button>
        ) : (
          <Button
            onClick={enableTwoFactor}
            disabled={isLoading}
            className="genova-button w-full"
          >
            <Shield className="w-4 h-4 mr-2" />
            {isLoading ? 'Setting up...' : 'Enable 2FA'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorAuth;
